import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw, Search, Archive, User } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { friendlyErr } from "../lib/friendlyErr";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { isoToDisplayDateTime } from "../components/DateInput";

const fmt = (iso) => isoToDisplayDateTime(iso);
const RESTORE_MSG = { 401: "Sesi Anda telah berakhir. Silakan login kembali.", 403: "Anda tidak memiliki hak untuk memulihkan data jemaat.", 404: "Data jemaat tidak ditemukan.", 409: "Data jemaat sudah dipulihkan." };

export default function JemaatTerhapusPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, bulan_ini: 0 });
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 350); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/jemaat/terhapus", { params: { search: query || undefined, page_size: 50 } }),
        api.get("/jemaat/terhapus/stats"),
      ]);
      setItems(r.data.items); setTotal(r.data.total); setStats(s.data);
    } catch (e) { toast.error(friendlyErr(e)); } finally { setLoading(false); }
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const restore = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post(`/jemaat/${target.id}/restore`);
      toast.success("Data jemaat berhasil dipulihkan.");
      setTarget(null); load();
    } catch (e) { toast.error(friendlyErr(e, RESTORE_MSG)); } finally { setSaving(false); }
  };

  const RestoreBtn = ({ j, full }) => (
    <Button variant="outline" className={`rounded-xl min-h-[44px] ${full ? "w-full" : ""}`} onClick={() => setTarget(j)} data-testid={`restore-btn-${j.id}`}>
      <RotateCcw className="h-4 w-4 mr-2" /> Pulihkan
    </Button>
  );
  const Foto = ({ j, size }) => (
    <div className={`${size} rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 shrink-0`}>
      {j.foto_path ? <img src={fileUrl(j.foto_path)} alt="" className="h-full w-full object-cover" /> : <User className="h-5 w-5" />}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="jemaat-terhapus-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Archive className="h-6 w-6 text-slate-500" /> Data Jemaat Terhapus</h1>
        <p className="text-sm text-slate-500 mt-1">Daftar jemaat yang telah dihapus menggunakan Soft Delete. Data masih tersimpan dan dapat dipulihkan oleh Admin.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <Card className="p-4 rounded-2xl"><div className="text-2xl font-bold text-slate-900" data-testid="terhapus-total">{stats.total}</div><div className="text-xs text-slate-500">Total Data Terhapus</div></Card>
        <Card className="p-4 rounded-2xl"><div className="text-2xl font-bold text-slate-900" data-testid="terhapus-bulan-ini">{stats.bulan_ini}</div><div className="text-xs text-slate-500">Terhapus Bulan Ini</div></Card>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari NIJ / Nama Jemaat" className="pl-9 min-h-[44px] rounded-xl" data-testid="terhapus-search" />
      </div>
      {loading ? <p className="text-sm text-slate-400" data-testid="terhapus-loading">Memuat...</p> : items.length === 0 ? (
        <Card className="p-8 rounded-2xl text-center text-slate-400" data-testid="terhapus-empty">Tidak ada jemaat terhapus{query ? ` untuk "${query}"` : ""}.</Card>
      ) : (
        <>
          <Card className="hidden md:block rounded-2xl overflow-hidden" data-testid="terhapus-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>{["No", "Foto", "NIJ", "Nama Jemaat", "Status", "Dihapus Pada", "Dihapus Oleh", "Aksi"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {items.map((j, i) => (
                  <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`terhapus-item-${j.id}`}>
                    <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                    <td className="px-4 py-3"><Foto j={j} size="h-10 w-10" /></td>
                    <td className="px-4 py-3 font-mono text-xs">{j.nij}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900"><button type="button" className="hover:underline text-left" onClick={() => navigate(`/jemaat/${j.id}`)}>{j.nama_lengkap}</button></td>
                    <td className="px-4 py-3"><span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700">Terhapus</span></td>
                    <td className="px-4 py-3 text-slate-600">{fmt(j.deleted_at)}</td>
                    <td className="px-4 py-3 text-slate-600">{j.deleted_by || "-"}</td>
                    <td className="px-4 py-3"><RestoreBtn j={j} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <div className="md:hidden space-y-3" data-testid="terhapus-list">
            {items.map((j) => (
              <Card key={j.id} className="p-4 rounded-2xl space-y-3" data-testid={`terhapus-card-${j.id}`}>
                <div className="flex items-center gap-3" onClick={() => navigate(`/jemaat/${j.id}`)}>
                  <Foto j={j} size="h-14 w-14" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{j.nama_lengkap}</div>
                    <div className="font-mono text-xs text-slate-500">NIJ: {j.nij}</div>
                    <div className="text-xs mt-1"><span className="font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700">Status: Terhapus</span></div>
                  </div>
                </div>
                <div className="text-xs text-slate-500 leading-relaxed">Dihapus: {fmt(j.deleted_at)}<br />Oleh: {j.deleted_by || "-"}</div>
                <RestoreBtn j={j} full />
              </Card>
            ))}
          </div>
          <p className="text-xs text-slate-400">Menampilkan {items.length} dari {total} data</p>
        </>
      )}

      <Dialog open={Boolean(target)} onOpenChange={(o) => !o && !saving && setTarget(null)}>
        <DialogContent className="rounded-2xl max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle>Pulihkan Data Jemaat?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>Apakah Anda yakin ingin memulihkan data jemaat:</p>
                <p className="rounded-xl bg-slate-50 p-3"><span className="block">Nama: <b className="text-slate-900">{target?.nama_lengkap}</b></span><span className="block">NIJ: <b className="font-mono text-slate-900">{target?.nij}</b></span></p>
                <p>Setelah dipulihkan, data jemaat akan kembali muncul pada Data Jemaat aktif.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="rounded-xl min-h-[44px] flex-1" onClick={() => setTarget(null)} disabled={saving} data-testid="restore-cancel-btn">Batal</Button>
            <Button className="rounded-xl min-h-[44px] flex-1 bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={restore} disabled={saving} data-testid="restore-confirm-btn">{saving ? "Memulihkan..." : "Ya, Pulihkan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
